import { Steps, Typography, Tooltip, Tag } from 'antd';
import { LoadingOutlined, WifiOutlined, DisconnectOutlined } from '@ant-design/icons';
import type { WorkflowProgressState, WorkflowStep } from './useWorkflowProgress';
import { colors } from '@/theme/tokens';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// WorkflowProgressView — cookie-cutter realtime stepper for AIProcess workflows
// ─────────────────────────────────────────────────────────────────────────────
//
// Pairs with useWorkflowProgress. Renders the stepper schema you passed,
// lights up steps as their AddLog messages arrive, and shows the latest
// agent log line below the active step.
//
// REUSE
//   Any feature that triggers a workflow can drop this in:
//     <WorkflowProgressView state={progress} steps={STEPS} isDark={isDark} />
//
//   The component is presentational — the data lifecycle lives in the
//   hook. Color palette uses Agent Studio's tokens so the look is
//   consistent across drawers/pages.
// ─────────────────────────────────────────────────────────────────────────────

export interface WorkflowProgressViewProps {
  state: WorkflowProgressState;
  steps: WorkflowStep[];
  isDark: boolean;
  /** Optional helper text shown above the stepper. */
  caption?: string;
}

export function WorkflowProgressView({
  state,
  steps,
  isDark,
  caption,
}: WorkflowProgressViewProps) {
  // Build Ant Design Steps items with per-step status:
  //   - completed steps: 'finish' (checkmark)
  //   - active step:    'process' (spinner via custom icon)
  //   - upcoming steps: 'wait'
  // For the failed terminal state, mark the active step 'error'.
  const items = steps.map((step) => {
    const isCompleted = state.completedSteps.has(step.id);
    const isActive = state.activeStepId === step.id;

    let status: 'wait' | 'process' | 'finish' | 'error' = 'wait';
    if (isCompleted) status = 'finish';
    else if (isActive)
      status = state.status === 'failed' ? 'error' : 'process';

    return {
      title: step.label,
      description: isActive ? step.description : undefined,
      status,
      icon: isActive && state.status !== 'failed' ? <LoadingOutlined /> : undefined,
    };
  });

  const tone =
    state.status === 'failed'
      ? colors.red[500]
      : state.signalRConnected
        ? colors.green[500]
        : colors.amber[500];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '16px 14px',
        borderRadius: 8,
        border: `1px solid ${isDark ? colors.slate[700] : colors.ink[100]}`,
        background: isDark ? colors.slate[800] : colors.ink[50],
      }}
    >
      {(caption || true) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Text strong style={{ fontSize: 13 }}>
            {caption ?? 'Workflow progress'}
          </Text>
          <Tooltip
            title={
              state.signalRConnected
                ? 'Live: receiving step updates over SignalR'
                : 'SignalR not connected — falling back to polling'
            }
          >
            <Tag
              icon={state.signalRConnected ? <WifiOutlined /> : <DisconnectOutlined />}
              color="default"
              style={{
                background: 'transparent',
                borderColor: tone,
                color: tone,
                fontSize: 11,
              }}
            >
              {state.signalRConnected ? 'live' : 'polling'}
            </Tag>
          </Tooltip>
        </div>
      )}

      <Steps
        size="small"
        direction="vertical"
        // Ant Design's `current` prop — pin to the active index for the
        // animation, fall back to the highest finished index, or 0.
        current={Math.max(
          steps.findIndex((s) => s.id === state.activeStepId),
          steps.filter((s) => state.completedSteps.has(s.id)).length - 1,
          0,
        )}
        items={items}
      />

      {state.latestLog && (
        <div
          style={{
            fontSize: 12,
            fontStyle: 'italic',
            color: isDark ? colors.slate[400] : colors.slate[500],
            padding: '6px 10px',
            borderLeft: `2px solid ${isDark ? colors.slate[600] : colors.slate[300]}`,
          }}
        >
          {state.latestLog}
        </div>
      )}
    </div>
  );
}
